<template>
  <navi-modal
    block-scroll
    no-padding
    no-background
    full
    v-model="computedShow"
    :animation="animation"
    :title="newProfile ? $t('components.Profile.welcomeTitle') : $t('components.SettingsControl.manageProfile')"
    :showSettingsMobile.sync="showSettingsMobile"
    :unclosable="unclosable"
  >
    <template v-slot:header v-if="!screen.breakPoint.tabPort">
      {{ formFields && formFields[activeSection] && simpleNav.current ? simpleNav.current.name : '' }}
    </template>

    <template v-slot:headerControls v-if="newProfile">
      <a href="#" @click.prevent="logout">
        <i class="icon-logout mr-05"></i> {{screen.breakPoint.tabPort ? $t('components.ProfileStatus.logout') : ''}}
      </a>
    </template>

    <template v-slot:navigation>
      <navi-modal-item
        v-for="(item, index) in formSectionsFiltered"
        :key="index"
        @click="setActiveTab(item.key)"
        :active="screen.breakPoint.tabPort && item.active"
        :disabled="item.disabled"
      >
        <div class="d-flex align-center">
          <div class="heading-5 text--medium pr-1 mr-05" :class="item.disabled ? 'text-color-grey-light' : ''">{{index + 1}}.</div>
          <div class="text--regular">
            <span :class="item.disabled ? 'text-color-grey' : (item.hasErrors ? 'text-color-error' : '')">{{item.name}}</span>
            <br>
            <i v-if="item.description" class="text-color-grey">({{item.description}})</i>
          </div>
<!--          <div class="icon-checked ml-1" v-show="item.ready && !item.optional"><span class="icon-check text-color-grey-darken text&#45;&#45;xs-small"></span></div>-->
          <div class="ml-auto"></div>
          <div class="icon-open rotate--90 text--large" v-show="!screen.breakPoint.tabPort"><span class="icon-show text-color-space"></span></div>
        </div>
      </navi-modal-item>
    </template>

    <div class="mx-auto pa-2" :style="`max-width: 70rem;`" v-if="statusNotificationMessage && formFields && formFields[activeSection]">
      <div class="mb-4">
        <i class="icon-attention text-color-error"></i> <span class="text-color-error mb-2">{{ statusNotificationMessage.text }}</span>
        <v-button @click="setCorrectionStatus()" :loading="loadingCorrection" class="mt-1" size="small" color="success" v-if="profile.status === 'READY' || profile.status === 'VERIFICATION'">{{ $t('components.Profile.returnToEdits') }}</v-button>
      </div>
      <div class="heading-4 mb-2" v-if="screen.breakPoint.tabPort && formFields[activeSection]">
        {{formFields[activeSection].name}}
      </div>
      <div class="form-field-group__description mb-1" v-if="formFields[activeSection]">
        {{formFields[activeSection].description}}
      </div>
      <div v-for="(field, index) of fieldsFiltered" :key="`${index}-${field.sectionKey}-${field.key}`" class="form-field-group">
        <div class="form-field-group__title" v-if="field.title">{{field.title}}</div>
        <component :is="field.component" v-bind="{ ...field.props, value: form[field.key], error: formErrors[field.key] }" v-on="field.events" :req-field-name="field.key"></component>
<!--        <div class="form-field-group__optional-desc d-flex justify-start" v-if="!field.required && (field.optionalDesc || typeof field.optionalDesc === 'undefined')"><i>{{$t('components.Profile.optional')}}</i></div>-->
        <div class="form-field-group__description" v-if="field.description">{{field.description}}</div>
      </div>
    </div>

    <template v-slot:globalFooter v-if="!screen.breakPoint.tabPort">
      <div class="d-flex mx-auto" :style="`max-width: 70rem;`" v-if="showSettingsMobile && formFields && formFields[activeSection]">
        <div class="ml-auto"></div>
        <v-button v-if="simpleNav" @click="goPrevious(simpleNav.previous.key)" :disabled="!simpleNav.previous.key && true" outline>
          <span class="d-flex align-center justify-center">
            <i class="d-block icon-show rotate-90" style="margin-top: 0.1rem;"></i>
          </span>
        </v-button>
        <v-button v-if="simpleNav" @click="goNext(simpleNav.next.key)" :disabled="!simpleNav.next.key" class="ml-05">
          <span class="d-flex align-center justify-center">
            <span class="d-block text--xs-small">{{ $t('components.Profile.forward') }}</span>
            <i class="d-block icon-show rotate--90" style="margin-top: 0.1rem;"></i>
          </span>
        </v-button>

        <v-button v-if="newProfile" @click="sendForm()" class="ml-05" :inactive="fieldsStat.required.filled !== fieldsStat.required.count && fieldsStat.errors.count > 0" color="success" :loading="loading">
          {{ $t('components.Profile.send') }}
        </v-button>

        <v-button v-else-if="formChanged && profileProp.status === 'CORRECTION'" @click="sendForm()" outline class="ml-05" :loading="loading" color="info">
          {{ $t('widgets.Dashboard.EditWidget.save') }}
        </v-button>
        <v-button v-else-if="(formSaved && profileProp.status === 'CORRECTION') || profileProp.status !== 'CORRECTION'" :disabled="profileProp.status !== 'CORRECTION'" @click="setVerificationStatus()" class="ml-05" color="success" :loading="loadingVerification">
          {{ $t('components.Profile.send') }}
        </v-button>
      </div>
      <div class="d-flex direction-column" v-else>
        <v-button v-if="newProfile" @click="sendForm()" :inactive="fieldsStat.errors.count > 0 || fieldsStat.required.filled !== fieldsStat.required.count" width="full" color="success">{{ $t('components.Profile.send') }}</v-button>
        <v-button v-else-if="formChanged && profileProp.status === 'CORRECTION'" @click="sendForm()" width="full" outline class="ml-05" :loading="loading" color="info">
          {{ $t('widgets.Dashboard.EditWidget.save') }}
        </v-button>
        <v-button v-else-if="(formSaved && profileProp.status === 'CORRECTION')" @click="setVerificationStatus()" width="full" class="ml-05" color="success" :loading="loadingVerification">
          {{ $t('components.Profile.send') }}
        </v-button>
        <v-button v-else-if="profileProp.status !== 'CORRECTION'" @click="setCorrectionStatus()" :disabled="profileProp.status === 'OUT_PLATFORM'" width="full" class="ml-05" color="success" :loading="loadingCorrection">
          {{ $t('components.Profile.returnToEdits') }}
        </v-button>

        <div class="text-color-space text--center mt-1" v-if="fieldsStat.required.filled !== fieldsStat.required.count"
             v-html="$t('components.Profile.requiredFieldsFilledNum', { filledNum: fieldsStat.required.filled, requiredNum: fieldsStat.required.count })">
        </div>
      </div>
    </template>
    <template v-slot:footer v-else>
      <div class="d-flex mx-auto px-2 align-center" :style="`max-width: 70rem;`" v-if="formFields && formFields[activeSection]">
        <div class="text-color-space" v-html="$t('components.Profile.requiredFieldsFilledNum', { filledNum: fieldsStat.required.filled, requiredNum: fieldsStat.required.count })"></div>
        <div class="ml-auto"></div>
        <v-button v-if="simpleNav" @click="goPrevious(simpleNav.previous.key)" :disabled="!simpleNav.previous.key && true" outline>
          <span class="d-flex align-center justify-center">
            <i class="d-block icon-show rotate-90" style="margin-top: 0.1rem;"></i>
          </span>
        </v-button>
        <v-button v-if="simpleNav" @click="goNext(simpleNav.next.key)" :disabled="!simpleNav.next.key" class="ml-05">
          <span class="d-flex align-center justify-center">
            <span class="d-block text--xs-small">{{ $t('components.Profile.forward') }}</span>
            <i class="d-block icon-show rotate--90" style="margin-top: 0.1rem;"></i>
          </span>
        </v-button>
        <v-button v-if="newProfile" @click="sendForm()" class="ml-05" :loading="loading" :inactive="fieldsStat.errors.count > 0 || fieldsStat.required.filled !== fieldsStat.required.count" color="success">
          {{ $t('components.Profile.sendForVerification') }}
        </v-button>

        <v-button v-else @click="sendForm()" outline class="ml-05" :disabled="!formChanged || profileProp.status !== 'CORRECTION'" :loading="loading" color="info">
          {{ $t('widgets.Dashboard.EditWidget.save') }}
        </v-button>
        <v-button v-if="!newProfile" @click="setVerificationStatus()" class="ml-05" :loading="loadingVerification" :disabled="!formSaved || profileProp.status !== 'CORRECTION'" color="success">
          {{ $t('components.Profile.sendForVerification') }}
        </v-button>
      </div>
    </template>
  </navi-modal>
</template>

<script>
import Vue from 'vue'

import scrollIntoView from 'scroll-into-view-if-needed'

import vModal from '@/ui/modals/vModal'
import NaviModal from '@/ui/modals/NaviModal'
import NaviModalItem from '@/ui/modals/NaviModalItem'
import vButton from '@/ui/controls/vButton'

import screen from '@/mixins/screen'
import vModel from '@/mixins/vModel'
import confirmAction from '@/mixins/confirmAction'
import statusNotificationMessage from '@/mixins/statusNotificationMessage'
import { notifyGraphqlError } from '@/mixins/handleGraphqlError'
import { removePinToken } from '@/mixins/pinToken'

import formFields from './formFields'
import addMerchant from './addMerchant'
import mapFields from './mapFields'

import { AddMerchantGQL, ChangeProfileStatusGQL, UpdateMerchantGQL } from '@/graphql/mutations'
import { onLogout } from '@/vue-apollo'
import { MeGQL } from '@/graphql/queries'

export default {
  apollo: {
    me: {
      query: MeGQL
    }
  },

  props: {
    unclosable: {
      type: Boolean,
      default: false
    },

    profileProp: {
      type: Object,
      default: null
    },

    defaultSection: {
      type: String,
      default: null
    },

    animation: {
      type: String,
      default: 'slide-up'
    },

    newProfile: {
      type: Boolean,
      default: false
    }
  },

  components: {
    NaviModal,
    NaviModalItem,
    vButton,
    vModal
  },

  mixins: [
    screen,
    addMerchant,
    formFields,
    vModel,
    confirmAction,
    mapFields,
    statusNotificationMessage,
    notifyGraphqlError,
    removePinToken
  ],

  data () {
    return {
      form: {},
      formErrors: {},
      formSaved: true,
      formChanged: false,

      show: false,
      showModal: false,
      showSettingsMobile: false,

      activeSection: null,
      activeField: null,

      loading: false,
      loadingVerification: false,
      loadingCorrection: false,

      formInitialized: false
    }
  },

  methods: {
    async logoutExec () {
      await this.removePinToken()
      await onLogout(this.$apollo.getClient())
      await this.$router.push({
        name: 'auth_login'
      })
      this.$emit('input', false)
    },

    logout () {
      this.confirmAction(this.$i18n.t('components.ProfileStatus.areYouSureToLogout'), () => { this.logoutExec() })
    },

    setCorrectionStatus () {
      this.confirmAction(this.$t('components.Profile.confirmCorrectionMessage'), async () => {
        try {
          this.loadingCorrection = true
          await this.$apollo.mutate({
            mutation: ChangeProfileStatusGQL,
            variables: {
              params: {
                status: 'CORRECTION'
              }
            }
          })

          this.formSaved = true
          this.formChanged = false
          this.formInitialized = false
          setTimeout(() => {
            this.formInitialized = true
            this.formSaved = true
            this.formChanged = false
            this.loadingCorrection = false
          }, 1000)
        } catch (e) {
          this.loadingCorrection = false
          this.notifyGraphqlError(e)
        }
      })
    },

    async formInit () {
      for (const field of this.formFieldsRow) {
        Vue.set(this.form, field.key, field.default)
        Vue.set(this.formErrors, field.key, null)
      }

      const sectionsList = Object.keys(this.formFields)

      if (!this.activeSection && sectionsList.length) {
        this.activeSection = sectionsList[0]
      }

      const draftRow = await this.$storage.get({
        key: 'draftMerchant'
      })

      let draft
      if (this.newProfile) {
        const val = draftRow.value ? JSON.parse(draftRow.value) : {}
        if (val[this.me.id.toString()]) {
          draft = val[this.me.id.toString()]
        } else {
          draft = {}
        }
      } else if (this.profileProp) {
        draft = this.serializeFields(this.profileProp)
      } else {
        draft = {}
      }

      for (const key of Object.keys(draft)) {
        this.form[key] = draft[key]
      }

      if (!this.form.recruitedUser) {
        const inviter = await this.$storage.get({ key: 'inviter' }).then(res => res.value)
        this.form.recruitedUser = inviter || this.form.recruitedUser
      }

      if (this.formInitialized) {
        this.checkValidationErrors()
      }
      setTimeout(() => {
        this.formInitialized = true
      }, 1000)
    },

    onInput (e, key) {
      this.form[key] = e
      this.formErrors[key] = null
      // TODO refactor to fields options
      if (
        this.reqCountry.USA &&
        (key === 'online' || key === 'offline') &&
        (this.form.online || this.form.offline)
      ) {
        this.formErrors.online = null
        this.formErrors.offline = null
      }
    },

    onFocus (e, key) {},

    onFill (e) {
      for (const key of Object.keys(e)) {
        this.form[key] = e[key]
        this.formErrors[key] = null
      }
    },

    onBlur (e, key, validateFunc) {
      this.checkValidation(e, key, validateFunc)
    },

    setActiveTab (key) {
      this.activeSection = key
      this.showSettingsMobile = true
    },

    sectionKeyByField (key) {
      const f = this.formFieldsRow.find(f => {
        return f.key === key
      })

      return f ? f.sectionKey : null
    },

    goToError () {
      const key = Object.keys(this.formErrors).find(key => {
        return this.formErrors[key] && true
      })

      this.activeSection = this.sectionKeyByField(key) ? this.sectionKeyByField(key) : this.activeSection
      this.showSettingsMobile = true

      setTimeout(() => {
        const el = document.querySelector('.has-error')
        if (!el) {
          return
        }

        scrollIntoView(el, {
          behavior: 'smooth',
          scrollMode: 'if-needed',
          block: 'center',
          inline: 'nearest'
        })
      }, 200)
    },

    checkRequired () {
      if (this.fieldsStat.required.remained.count > 0) {
        this.$notify({
          type: 'error',
          message: this.$t('components.Profile.requiredDesc')
        })

        this.fieldsStat.required.remained.keys.forEach(key => {
          this.formErrors[key] = this.$t('components.Profile.required')
        })

        return true
      }

      return false
    },

    checkRequiredUpdated () {
      const formFieldsRowObject = {}
      this.formFieldsRow.forEach(item => {
        formFieldsRowObject[item.key] = item
      })
      Object.keys(this.formErrors).forEach(key => {
        if (this.formErrors[key] === this.$t('components.Profile.required') && !formFieldsRowObject[key].required) {
          this.formErrors[key] = null
        }
      })
    },

    checkValidation (e, key, validateFunc) {
      const res = []
      if (validateFunc && typeof validateFunc === 'function' && validateFunc(this.form[key])) {
        if (this.formErrors[key]) {
          return
        }
        const validateRes = validateFunc(this.form[key])
        if (Array.isArray(validateRes)) {
          for (const v of validateRes) {
            if (v) {
              res.push(v)
            }
          }
        } else {
          res.push(validateFunc(this.form[key]))
        }

        if (res.length > 1) {
          this.formErrors[key] = res.join(', ')
        } else if (res.length === 1) {
          this.formErrors[key] = res[0]
        } else {
          this.formErrors[key] = null
        }
      }

      return res.length && true
    },

    checkValidationErrors () {
      let errors = 0
      this.showingFields.forEach(field => {
        if (this.checkValidation(null, field.key, field.validate)) {
          errors++
        }
      })

      return errors > 0
    },

    sendForm () {
      const hasRequired = this.checkRequired()
      const hasCheckValidationErrors = this.checkValidationErrors()
      if (hasRequired || hasCheckValidationErrors) {
        this.$notify({
          type: 'error',
          message: this.$t('components.Profile.theFormContainsErrors')
        })
        this.goToError()
        return
      }

      this.confirmAction(this.newProfile ? this.$t('components.Profile.areYouSureToSubmit') : this.$t('components.Profile.areYouSureToSave'), async () => {
        this.loading = true

        const params = this.mapFields(this.form)
        try {
          if (this.newProfile) {
            await this.$apollo.mutate({
              mutation: AddMerchantGQL,
              variables: {
                params: params,
                draft: false
              }
            })

            await this.$router.push({
              name: 'dashboard'
            })
            this.$emit('input', false)
          } else {
            this.formInitialized = false
            await this.$apollo.mutate({
              mutation: UpdateMerchantGQL,
              variables: {
                params: params
              }
            })
          }

          setTimeout(() => {
            this.formSaved = true
            this.formChanged = false
          }, 500)
          this.loading = false
          await this.$apollo.getClient().reFetchObservableQueries(true)
        } catch (e) {
          this.loading = false
          this.notifyGraphqlError(e)
          // this.$notify({
          //   type: 'error',
          //   message: this.$t('errors.code.couldntSendTheForm')
          // })
          throw new Error(e)
        }
      })
    },

    setVerificationStatus () {
      this.confirmAction(this.$t('components.Profile.confirmVerificationMessage'), async () => {
        this.loadingVerification = true
        try {
          await this.$apollo.mutate({
            mutation: ChangeProfileStatusGQL,
            variables: {
              params: {
                status: 'VERIFICATION'
              }
            }
          })

          this.loadingVerification = false
        } catch (e) {
          this.loadingVerification = false
          this.notifyGraphqlError(e)
        }
      })
    },

    goNext (key) {
      this.activeSection = key
    },

    goPrevious (key) {
      this.activeSection = key
    },
    // TODO Вынести в утилсу
    isNullObject (obj) {
      let isNullObject = true
      for (const key of Object.keys(obj)) {
        if (obj[key] !== null && obj[key] !== undefined) {
          isNullObject = false
        }
      }
      return isNullObject
    }
  },

  watch: {
    form: {
      async handler (val) {
        if (this.formInitialized) {
          this.formSaved = false
          this.formChanged = true
        }

        if (!this.formInitialized || !this.newProfile) {
          return
        }

        this.checkRequiredUpdated()

        const prevValsRow = await this.$storage.get({
          key: 'draftMerchant'
        })
        const prevVals = prevValsRow.value ? JSON.parse(prevValsRow.value) : {}
        prevVals[this.me.id.toString()] = val

        await this.$storage.set({
          key: 'draftMerchant',
          value: JSON.stringify(prevVals)
        })
      },
      deep: true
    },

    'me.id' (val, oldVal) {
      if (val !== oldVal && this.newProfile) {
        this.formInit()
      }
    },

    profileProp () {
      this.formInit()
    },

    computedShow (val) {
      const formSections = this.formSectionsFiltered.filter(sec => !sec.disabled)
      const index = formSections.findIndex(section => section.key === this.activeSection)

      if (val && this.defaultSection && (index > -1)) {
        this.activeSection = this.defaultSection
      }
    }
  },

  computed: {
    simpleNav () {
      const formSections = this.formSectionsFiltered.filter(sec => !sec.disabled)
      const index = formSections.findIndex(section => section.key === this.activeSection)

      return {
        next: {
          name: (index < formSections.length - 1) && formSections[index + 1] ? formSections[index + 1].name : null,
          key: (index < formSections.length - 1) && formSections[index + 1] ? formSections[index + 1].key : null
        },

        current: {
          name: formSections[index] ? formSections[index].name : 0,
          key: formSections[index] ? formSections[index].key : 0
        },

        previous: {
          name: (index > 0) && formSections[index - 1] ? formSections[index - 1].name : null,
          key: (index > 0) && formSections[index - 1] ? formSections[index - 1].key : null
        }
      }
    },

    computedShow: {
      get () {
        return this.value
      },

      set (val) {
        this.$emit('input', val)
      }
    },

    formFieldsRow () {
      const res = []
      Object.keys(this.formFields).forEach(sectionKey => {
        if (!this.formFields[sectionKey].fields) {
          throw new Error(`[${sectionKey}]: fields" property is required!`)
        }

        for (const key of Object.keys(this.formFields[sectionKey].fields)) {
          let show = true
          if (typeof this.formFields[sectionKey].fields[key].show === 'function') {
            show = this.formFields[sectionKey].fields[key].show()
          } else if (typeof this.formFields[sectionKey].fields[key].show === 'boolean') {
            show = this.formFields[sectionKey].fields[key].show
          }

          let required = false
          if (typeof this.formFields[sectionKey].fields[key].required === 'function') {
            required = this.formFields[sectionKey].fields[key].required()
          } else if (typeof this.formFields[sectionKey].fields[key].required === 'boolean') {
            required = this.formFields[sectionKey].fields[key].required
          }

          const events = {}

          if (this.formFields[sectionKey].fields[key].watchEvent) {
            events[this.formFields[sectionKey].fields[key].watchEvent] = (e) => this.onInput(e, key)
          } else {
            events.input = (e) => this.onInput(e, key)
          }

          events.focus = (e) => this.onFocus(e, key)
          events.blur = (e) => this.onBlur(e, key, this.formFields[sectionKey].fields[key].validate)
          events.fill = (e) => this.onFill(e)

          res.push({
            ...this.formFields[sectionKey].fields[key],
            key,
            sectionKey,
            show,
            events,
            required
          })
        }

        return res
      })

      return res
    },

    fieldsStat () {
      let generalCount = 0
      let generalFilled = 0
      let requiredFields = 0
      let filled = 0
      let errorCount = 0
      const keys = []
      const remainedKeys = []
      const errorKeys = []
      const errorSectionKeys = []

      const fields = this.showingFields
      const form = this.form
      const formErrors = this.formErrors

      for (const field of fields) {
        if (formErrors[field.key] && formErrors[field.key].length) {
          errorCount++
          errorKeys.push(field.key)
          if (!errorSectionKeys.some(s => s === field.sectionKey)) {
            errorSectionKeys.push(field.sectionKey)
          }
        }
        if (field.required && field.show) {
          requiredFields++
          keys.push(field.key)
          if (Array.isArray(form[field.key]) && form[field.key].length && !this.isNullObject(form[field.key][0])) {
            filled++
          } else if (typeof form[field.key] === 'string' && form[field.key].length) {
            filled++
          } else if (typeof form[field.key] === 'boolean' && form[field.key]) {
            filled++
          } else if (typeof form[field.key] !== 'string' && typeof form[field.key] !== 'boolean' && form[field.key] !== null && !Array.isArray(form[field.key])) {
            filled++
          } else {
            remainedKeys.push(field.key)
          }
        }

        if (field.show) {
          generalCount++
          if (Array.isArray(form[field.key]) && form[field.key].length) {
            generalFilled++
          } else if (typeof form[field.key] === 'string' && form[field.key].length) {
            generalFilled++
          } else if (typeof form[field.key] === 'boolean' && !field.required) {
            generalFilled++
          } else if (typeof form[field.key] === 'boolean' && field.required && form[field.key]) {
            generalFilled++
          } else if (typeof form[field.key] !== 'string' && typeof form[field.key] !== 'boolean' && form[field.key] !== null) {
            generalFilled++
          }
        }
      }

      return {
        count: generalCount,
        filled: generalFilled,
        required: {
          count: requiredFields,
          filled: filled,
          remained: {
            count: requiredFields - filled,
            keys: remainedKeys
          },
          keys: keys
        },
        errors: {
          count: errorCount,
          keys: errorKeys,
          sectionKeys: errorSectionKeys
        }
      }
    },

    fieldsFiltered () {
      return this.formFieldsRow.filter(item => {
        return item.sectionKey === this.activeSection && item.show
      })
    },

    showingFields () {
      return this.formFieldsRow.filter(item => {
        return item.show && !this.formFields[item.sectionKey].disabled
      })
    },

    computedActiveField () {
      return this.formFieldsRow.find(item => {
        return item.key === this.activeField
      })
    },

    formSectionsRow () {
      return Object.keys(this.formFields).map(key => {
        const currentFields = this.formFieldsRow.filter(item => {
          return item.sectionKey === key
        })

        const form = this.form
        let requiredFields = 0
        let filled = 0
        for (const field of currentFields) {
          if (field.required && field.show) {
            requiredFields++

            if (Array.isArray(form[field.key]) && form[field.key].length) {
              filled++
            } else if (typeof form[field.key] === 'string' && form[field.key].length) {
              filled++
            } else if (typeof form[field.key] === 'boolean' && form[field.key]) {
              filled++
            } else if (typeof form[field.key] !== 'string' && typeof form[field.key] !== 'boolean' && form[field.key] !== null) {
              filled++
            }
          }
        }

        let show = true
        if (typeof this.formFields[key].show === 'function') {
          show = this.formFields[key].show()
        } else if (typeof this.formFields[key].show === 'boolean') {
          show = this.formFields[key].show
        }

        let disabled = false
        if (typeof this.formFields[key].disabled === 'function') {
          disabled = this.formFields[key].disabled()
        } else if (typeof this.formFields[key].disabled === 'boolean') {
          disabled = this.formFields[key].disabled
        }

        let description = null
        if (disabled && typeof this.formFields[key].disabledDesc === 'function') {
          description = this.formFields[key].disabledDesc()
        } else if (disabled && typeof this.formFields[key].disabledDesc === 'string') {
          description = this.formFields[key].disabledDesc
        }

        const hasErrors = this.fieldsStat.errors.sectionKeys.some(f => f === key)

        return {
          ...this.formFields[key],
          key,
          show,
          active: this.activeSection === key,
          ready: requiredFields === filled,
          optional: requiredFields === 0,
          disabled,
          description,
          hasErrors
        }
      })
    },

    formSectionsFiltered () {
      return this.formSectionsRow.filter(section => {
        return section.show
      })
    }
  },

  async mounted () {
    if (this.me) {
      await this.formInit()
    }
  }
}
</script>

<style lang="scss">
.form-field-group {
  &:not(:last-child) {
    padding-bottom: 1.5rem;
  }

  &__title {
    color: $color-space;
    font-weight: 700;
    margin-bottom: 1.5rem;
    margin-top: 1rem;
  }

  &__description {
    color: $color-space;
    font-size: 1.3rem;
    margin-top: .5rem;
  }

  &__optional-desc {
    font-size: .9rem;
    font-weight: 600;

    @include respond(big-desktop) {
      font-size: .8rem;
    }
    color: $color-space;
    margin-top: .4rem;
    text-transform: uppercase;
  }
}

.icon-checked {
  //background-color: $color-success;
  //border-radius: 50%;
  //padding: .63rem .6rem;
}
</style>
